import React, { useRef, useState } from 'react';
import { FileSpreadsheet, Loader2, Upload, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import api from '@/lib/axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Two-step iMikel data-import dialog.
 *  1. user picks an .xlsx → POST /imports/imikel/preview
 *     server returns dry-run counts + a short-lived token
 *  2. user confirms        → POST /imports/imikel/commit (with the token)
 *
 * Only shown / opened by super-admin (or admin with write-access to the tenant).
 */
export default function ImikelImportDialog({ open, onOpenChange, tenant, onImported }) {
  const [phase, setPhase] = useState('select'); // select | previewing | preview | committing | done
  const [preview, setPreview] = useState(null); // stats from backend
  const [file, setFile] = useState(null);
  const [token, setToken] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const reset = () => {
    setPhase('select'); setPreview(null); setFile(null);
    setToken(null); setResult(null); setError(null);
  };

  const close = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const runPreview = async (f) => {
    if (!f) return;
    setFile(f); setError(null); setPhase('previewing');
    try {
      const form = new FormData();
      form.append('tenant_id', tenant.id);
      form.append('file', f);
      const res = await api.post('/imports/imikel/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setToken(res.data.token);
      setPhase('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Vorschau fehlgeschlagen');
      setPhase('select');
    }
  };

  const runCommit = async () => {
    if (!token) return;
    setPhase('committing'); setError(null);
    try {
      const form = new FormData();
      form.append('token', token);
      const res = await api.post('/imports/imikel/commit', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setPhase('done');
      toast.success(`Import erfolgreich: ${res.data.students_created} Schüler`);
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import fehlgeschlagen');
      setPhase('preview');
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="max-w-xl"
        data-testid="imikel-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-brand-red" strokeWidth={1.5} />
            iMikel-Daten importieren
          </DialogTitle>
          <DialogDescription>
            Mandant: <span className="font-semibold">{tenant?.name}</span> ({tenant?.slug})
          </DialogDescription>
        </DialogHeader>

        {phase === 'select' && (
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Wähle eine iMikel-Export-Datei (.xlsx). Die Vorschau zeigt, was importiert würde,
              bevor tatsächlich etwas geschrieben wird.
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              data-testid="imikel-file-input"
              onChange={(e) => runPreview(e.target.files?.[0])}
            />
            <Button
              onClick={() => inputRef.current?.click()}
              className="w-full"
              data-testid="imikel-pick-file"
            >
              <Upload className="w-4 h-4 mr-2" /> XLSX-Datei wählen…
            </Button>
            {error && (
              <p className="mt-3 text-sm text-red-600 flex items-center gap-2" data-testid="imikel-error">
                <AlertTriangle className="w-4 h-4" /> {error}
              </p>
            )}
          </div>
        )}

        {phase === 'previewing' && (
          <div className="py-10 flex flex-col items-center gap-2 text-slate-500">
            <Loader2 className="w-7 h-7 animate-spin" />
            <p className="text-sm">Datei wird analysiert…</p>
          </div>
        )}

        {phase === 'preview' && preview && (
          <div className="py-2" data-testid="imikel-preview">
            <p className="text-sm text-slate-600 mb-4">
              Vorschau für <span className="font-mono text-xs">{file?.name}</span> (
              {(file?.size / 1024).toFixed(0)} KB)
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Gelesene Zeilen" value={preview.total_rows} />
              <Stat label="Eindeutige Kassenzeichen" value={preview.unique_kassenzeichen} />
              <Stat label="Familien (neu)" value={preview.families_created} accent />
              <Stat label="Zahlungspflichtige" value={preview.guardians_created} accent />
              <Stat label="Schüler" value={preview.students_created} accent />
              <Stat label="Übersprungen" value={preview.skipped_rows} warn={preview.skipped_rows > 0} />
            </div>
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                Der Import fügt Datensätze <strong>hinzu</strong>. Falls der Mandant schon Daten
                enthält, prüfe vorher auf Duplikate. Dieser Schritt kann nicht einfach rückgängig
                gemacht werden.
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => close(false)} data-testid="imikel-cancel">
                Abbrechen
              </Button>
              <Button onClick={runCommit} data-testid="imikel-commit">
                Import jetzt ausführen
              </Button>
            </div>
          </div>
        )}

        {phase === 'committing' && (
          <div className="py-10 flex flex-col items-center gap-2 text-slate-500">
            <Loader2 className="w-7 h-7 animate-spin" />
            <p className="text-sm">Import läuft… das kann ein paar Sekunden dauern.</p>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="py-4" data-testid="imikel-done">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3">
              <CheckCircle2 className="w-5 h-5" />
              Import abgeschlossen
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="Familien angelegt" value={result.families_created} accent />
              <Stat label="Zahlungspflichtige" value={result.guardians_created} accent />
              <Stat label="Schüler" value={result.students_created} accent />
              <Stat label="Verknüpfungen" value={result.student_guardian_links} />
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => close(false)} data-testid="imikel-close">
                Schließen
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, accent = false, warn = false }) => (
  <div
    className={[
      'rounded-md border px-3 py-2',
      accent
        ? 'border-brand-red/20 bg-brand-red/5'
        : warn
          ? 'border-amber-200 bg-brand-red/5'
          : 'border-slate-200 bg-slate-50',
    ].join(' ')}
  >
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-lg font-semibold ${accent ? 'text-brand-red' : 'text-slate-800'}`}>
      {(value ?? 0).toLocaleString('de-DE')}
    </p>
  </div>
);
